import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useEffect, useRef, useState } from 'react';
import { trpc } from '../utils/trpc';
import style from '../styles/MapPage.module.css';
import VenueProfile from './VenueProfile';
import ReactDOM from 'react-dom';
import { VenueCard } from '../components/VenueCard';
import { GroupCard } from '../components/GroupCard';
import { EventCard } from '../components/EventCard';
import { useAuth } from '../contexts/AuthContext';
import FilterIcon from '../images/FilterIcon.png';

const SearchType = {
  Venues: 'venues',
  Events: 'events',
  Groups: 'groups',
} as const;

const SortType = {
  Popular: 'popular',
  ForYou: 'foryou',
} as const;

type SearchTypeValue = (typeof SearchType)[keyof typeof SearchType];
type SortTypeValue = (typeof SortType)[keyof typeof SortType];

export function MapPage() {
  const { session } = useAuth();
  const [isActive, setIsActive] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestedResults, setSuggestedResults] = useState([]) as any;
  const [sortType, setSortType] = useState<SortTypeValue>(SortType.Popular);
  const [selected, setSelected] = useState<{ venue_id: string } | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  const createCoords = (lat: number, lng: number) => ({ lat, lng });
  const [center, setCenter] = useState(createCoords(43.1566, -77.6088));
  const [searchType, setSearchType] = useState<SearchTypeValue>(SearchType.Venues);

  const handleSearchTypeChange = (type: SearchTypeValue) => {
    if (type != searchType) {
      setSuggestedResults([]);
      setSearchType(type);
    }
  };

  const getGroupPins = (group: any) => {
    return (group.event_venues ?? [])
      .filter((venue: any) => venue.location)
      .map((venue: any) => ({
        lat: venue.location.lat,
        lng: venue.location.lng,
        venue_id: venue.venue_id,
      }));
  };

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'groupsoup-google-maps-script',
    googleMapsApiKey: apiKey ?? '',
  });

  const venuePopularQuery = trpc.venue.getPopularVenues.useQuery(
    { regex: searchTerm, limit: 100 },
    { enabled: searchType === SearchType.Venues && sortType === SortType.Popular },
  );
  const venueForYouQuery = trpc.venue.getVenuesForYou.useQuery(
    { regex: searchTerm },
    { enabled: searchType === SearchType.Venues && sortType === SortType.ForYou },
  );

  const eventPopularQuery = trpc.event.popular.useQuery(
    { regex: searchTerm, limit: 100 },
    { enabled: searchType === SearchType.Events && sortType === SortType.Popular },
  );
  const eventForYouQuery = trpc.event.suggest.useQuery(
    { regex: searchTerm },
    { enabled: searchType === SearchType.Events && sortType === SortType.ForYou },
  );
  const groupPopularQuery = trpc.group.popular.useQuery(
    { regex: searchTerm },
    { enabled: searchType === SearchType.Groups && sortType === SortType.Popular },
  );

  const groupForYouQuery = trpc.group.suggest.useQuery(
    { regex: searchTerm },
    { enabled: searchType === SearchType.Groups && sortType === SortType.ForYou },
  );

  const suggestedRaw = (() => {
    switch (searchType) {
      case SearchType.Venues:
        return sortType === SortType.Popular
          ? venuePopularQuery.data
          : venueForYouQuery.data;
      case SearchType.Events:
        return sortType === SortType.Popular
          ? eventPopularQuery.data
          : eventForYouQuery.data;
      case SearchType.Groups:
        return sortType === SortType.Popular
          ? groupPopularQuery.data
          : groupForYouQuery.data;
      default:
        return null;
    }
  })();

  useEffect(() => {
    setSuggestedResults(suggestedRaw || []);
  }, [suggestedRaw, session, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loadError) {
    return <div>Error loading Google Maps: {loadError.message}</div>;
  }

  const venueMap = new Map(
    (sortType === SortType.Popular ? venuePopularQuery.data : venueForYouQuery.data)?.map(
      (venue: any) => [venue.venue_id, venue.location],
    ) ?? [],
  );

  const mapPins = (() => {
    let pins = [];
    if (searchType === SearchType.Groups) {
      pins = suggestedResults.flatMap(getGroupPins);
    } else {
      pins = suggestedResults
        .map((event: any) => {
          const location = venueMap.get(event.venue_id);
          if (!location) return null;
          return { lat: location.lat, lng: location.lng, venue_id: event.venue_id };
        })
        .filter(Boolean);
    }
    return [...new Map(pins.map((pin: any) => [pin.venue_id, pin])).values()] as {
      venue_id: string;
      lat: number;
      lng: number;
    }[];
  })();

  if (!isLoaded) {
    return <div>Loading map...</div>;
  }

  return (
    <div className={style.fullMap}>
      <GoogleMap
        mapContainerClassName={style.mapContainer}
        center={center}
        zoom={12}
        options={{
          zoomControlOptions: { position: google.maps.ControlPosition.TOP_CENTER },
          mapTypeControlOptions: { position: google.maps.ControlPosition.TOP_RIGHT },
          styles: [
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          ],
        }}
      >
        <div className={`${style.searchTab} ${isActive ? style.active : ''}`}>
          <button className={style.closeButton} onClick={() => setIsActive(false)}>
            ✕
          </button>
          <div className={style.searchBar} ref={filterRef}>
            <input
              onFocus={() => setIsActive(true)}
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${isActive ? style.active : ''}`}
            />
            <button
              className={style.filterButton}
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <img src={FilterIcon} className={style.filterIcon} />
            </button>
            {filterOpen && (
              <div className={style.filterMenu}>
                <label className={style.filterOption}>
                  <input
                    type="checkbox"
                    checked={sortType === SortType.Popular}
                    onChange={() => setSortType(SortType.Popular)}
                  />
                  Popular
                </label>
                <label className={style.filterOption}>
                  <input
                    type="checkbox"
                    checked={sortType === SortType.ForYou}
                    onChange={() => setSortType(SortType.ForYou)}
                  />
                  For You
                </label>
              </div>
            )}
          </div>
          <div className={style.searchTypeContainer}>
            <button
              className={`${style.searchTypeButton} ${searchType == 'venues' ? style.selected : ''}`}
              onClick={() => handleSearchTypeChange(SearchType.Venues)}
            >
              Venues
            </button>
            <button
              className={`${style.searchTypeButton} ${searchType == 'groups' ? style.selected : ''}`}
              onClick={() => handleSearchTypeChange(SearchType.Groups)}
            >
              Groups
            </button>
            <button
              className={`${style.searchTypeButton} ${searchType == 'events' ? style.selected : ''}`}
              onClick={() => handleSearchTypeChange(SearchType.Events)}
            >
              Events
            </button>
          </div>
          <div className={style.searchResults}>
            {(() => {
              switch (searchType) {
                case SearchType.Venues:
                  return suggestedResults.map((venue: { venue_id: string }) => (
                    <VenueCard key={venue.venue_id} id={venue.venue_id} />
                  ));
                case SearchType.Events:
                  return suggestedResults.map((event: any) => (
                    <EventCard
                      key={event.event_id}
                      event={event}
                      groupId={event.group_id}
                    />
                  ));
                case SearchType.Groups:
                  return suggestedResults.map((group: { id: string }) => (
                    <GroupCard key={group.id} id={group.id} />
                  ));
                default:
                  return '';
              }
            })()}
          </div>
        </div>
        {mapPins.map((pin: { venue_id: any; lat: any; lng: any }) => (
          <Marker
            key={`${pin.venue_id}`}
            position={{ lat: pin.lat, lng: pin.lng }}
            onClick={() => {
              setSelected({ venue_id: pin.venue_id });
              setCenter({ lat: pin.lat, lng: pin.lng });
            }}
          />
        ))}
      </GoogleMap>
      {selected &&
        ReactDOM.createPortal(
          <VenueProfile venueId={selected.venue_id} onClose={() => setSelected(null)} />,
          document.body,
        )}
    </div>
  );
}
